import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { authApi } from "../../services/api";

export default function LoginPage() {
  const { login, finalizeLogin } = useAuth();
  const navigate = useNavigate();

  const isExpired =
    new URLSearchParams(window.location.search).get("expired") === "1";

  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [otp, setOtp] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [qrCode, setQrCode] = useState(null);
  const [setupToken, setSetupToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if ((step === "otp" || step === "setup2fa") && otp.length !== 6) {
      setError("Please enter a valid 6-digit verification code.");
      return;
    }

    setLoading(true);

    try {
      if (step === "setup2fa") {
        const { data } = await authApi.verifySetup2FA(otp, setupToken);
        const res = data.data;

        finalizeLogin(
          res.token,
          res.refreshToken,
          { email: res.email, role: res.role },
          rememberMe
        );

        navigate("/", { replace: true });
        return;
      }

      const data = await login(
        email.trim(),
        password,
        step === "otp" ? otp : undefined,
        rememberMe
      );

      if (!data?.requires2FA && !data?.requires2FASetup) {
        navigate("/", { replace: true });
        return;
      }

      if (data?.requires2FA) {
        setStep("otp");
        setOtp("");
        setError("");
        return;
      }

      if (data?.requires2FASetup) {
        setQrCode(data.qrCodeBase64);
        setSetupToken(data.setupToken);
        setStep("setup2fa");
        setOtp("");
        setError("");
        return;
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Invalid credentials. Please check your email and password."
      );
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep("credentials");
    setOtp("");
    setQrCode(null);
    setSetupToken(null);
    setError("");
  };

  const TITLES = {
    credentials: "Welcome Back!",
    otp: "Two-Factor Auth",
    setup2fa: "Set Up 2FA",
  };

  const SUBTITLES = {
    credentials: "Enter your email and password to access admin panel",
    otp: "Enter the 6-digit code from your authenticator app",
    setup2fa: "Scan this QR code with Google Authenticator or Authy, then enter the code below",
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .lp-input {
          width: 100%;
          padding: 14px 18px;
          border-radius: 50px;
          background: #f5f7fa;
          border: 1.5px solid #f5f7fa;
          color: #1a1a2e;
          font-size: 14px;
          outline: none;
          font-family: 'DM Sans', sans-serif;
          transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .lp-input::placeholder { color: #aab0c0; }
        .lp-input:focus {
          border-color: #1a8a8a;
          background: #fff;
          box-shadow: 0 0 0 4px rgba(26,138,138,0.1);
        }
        .lp-btn {
          width: 100%;
          padding: 15px;
          border-radius: 50px;
          border: none;
          cursor: pointer;
          background: linear-gradient(135deg, #1a8a8a 0%, #25b0a8 100%);
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          font-family: 'DM Sans', sans-serif;
          letter-spacing: 0.3px;
          transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 4px 20px rgba(26,138,138,0.45);
        }
        .lp-btn:hover:not(:disabled) {
          opacity: 0.92;
          transform: translateY(-1px);
          box-shadow: 0 6px 28px rgba(26,138,138,0.55);
        }
        .lp-btn:active:not(:disabled) { transform: translateY(0); }
        .lp-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .lp-check {
          width: 16px; height: 16px;
          accent-color: #1a8a8a; cursor: pointer;
        }
        .lp-link-btn {
          background: none; border: none;
          color: #1a8a8a; cursor: pointer;
          font-size: 13px; font-weight: 500;
          text-align: center;
          font-family: 'DM Sans', sans-serif;
        }
        .lp-qr-box {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 20px;
          background: #f5f7fa;
          border-radius: 16px;
          border: 1.5px solid #e8ecf0;
        }
        .lp-qr-box img {
          width: 180px; height: 180px;
          border-radius: 12px;
          border: 2px solid #e8ecf0;
        }
        .lp-qr-hint {
          font-size: 12px;
          color: #7a8499;
          text-align: center;
          max-width: 280px;
          line-height: 1.5;
        }
        @media (max-width: 992px) {
          .lp-right-panel { display: none !important; }
          .lp-left-panel { flex: 1 1 100% !important; }
        }
      `}</style>

      <div
        className="lp-left-panel"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 40px",
          background: "#ffffff",
        }}
      >
        <div style={{ width: "100%", maxWidth: 480 }}>
          <div style={{
            width: "100%", padding: "18px 24px",
            border: "1.5px solid #e8ecf0", borderRadius: 12,
            marginBottom: 36, textAlign: "center",
            color: "#aab0c0", fontSize: 13, fontWeight: 500,
            letterSpacing: "2px", textTransform: "uppercase",
          }}>
            ASTROVEDA
          </div>

          <h1 style={{ fontSize: 36, fontWeight: 800, color: "#1a1a2e", marginBottom: 10, lineHeight: 1.2 }}>
            {TITLES[step]}
          </h1>
          <p style={{ fontSize: 14, color: "#7a8499", marginBottom: 36, lineHeight: 1.5 }}>
            {SUBTITLES[step]}
          </p>

          {isExpired && !error && step === "credentials" && (
            <div style={{
              background: "#fffbeb", border: "1.5px solid #fcd34d",
              borderRadius: 10, padding: "12px 16px", marginBottom: 20,
              display: "flex", alignItems: "center", gap: 10,
              color: "#92400e", fontSize: 13, fontWeight: 500,
            }}>
              <span style={{ fontSize: 16 }}>⏱</span>
              Your session has expired. Please sign in again.
            </div>
          )}

          {error && (
            <div style={{
              background: "#fff5f5", border: "1.5px solid #fecaca",
              borderRadius: 10, padding: "12px 16px", marginBottom: 20,
              color: "#dc2626", fontSize: 13, fontWeight: 500,
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {step === "credentials" && (
              <>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#1a1a2e", marginBottom: 8 }}>
                    Email
                  </label>
                  <input
                    className="lp-input"
                    type="email"
                    required
                    autoComplete="username"
                    placeholder="Enter email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#1a1a2e", marginBottom: 8 }}>
                    Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      className="lp-input"
                      type={showPwd ? "text" : "password"}
                      required
                      autoComplete="current-password"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{ paddingRight: 70 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      style={{
                        position: "absolute", right: 20, top: "50%",
                        transform: "translateY(-50%)", background: "none",
                        border: "none", color: "#7a8499", cursor: "pointer",
                        fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {showPwd ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input
                    type="checkbox"
                    id="remember"
                    className="lp-check"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <label htmlFor="remember" style={{ fontSize: 14, color: "#4a5568", cursor: "pointer", userSelect: "none" }}>
                    Remember me
                  </label>
                </div>
              </>
            )}

            {step === "otp" && (
              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#1a1a2e", marginBottom: 8 }}>
                  Verification Code
                </label>
                <input
                  className="lp-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  required
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, ""));
                    if (error) setError("");
                  }}
                  style={{ textAlign: "center", letterSpacing: "10px", fontSize: 22, fontWeight: 700 }}
                />
              </div>
            )}

            {step === "setup2fa" && (
              <>
                {qrCode && (
                  <div className="lp-qr-box">
                    <img
                      src={`data:image/png;base64,${qrCode}`}
                      alt="Scan with your authenticator app"
                    />
                    <p className="lp-qr-hint">
                      Open <strong>Google Authenticator</strong> or <strong>Authy</strong>, tap <em>Add account</em>, then scan this QR code.
                    </p>
                  </div>
                )}

                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#1a1a2e", marginBottom: 8 }}>
                    Enter the 6-digit code to confirm
                  </label>
                  <input
                    className="lp-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    placeholder="000000"
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/\D/g, ""));
                      if (error) setError("");
                    }}
                    style={{ textAlign: "center", letterSpacing: "10px", fontSize: 22, fontWeight: 700 }}
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              className="lp-btn"
              disabled={loading || ((step === "otp" || step === "setup2fa") && otp.length !== 6)}
              style={{ marginTop: 4 }}
            >
              {loading
                ? "Please wait…"
                : step === "otp"
                  ? "Verify & Sign In"
                  : step === "setup2fa"
                    ? "Activate & Sign In"
                    : "Sign In"}
            </button>

            {step !== "credentials" && (
              <button type="button" onClick={goBack} className="lp-link-btn">
                ← Back to login
              </button>
            )}
          </form>

          <p style={{ marginTop: 36, fontSize: 13, color: "#aab0c0", textAlign: "center" }}>
            © 2021 – 2026 All Rights Reserved.
          </p>
        </div>
      </div>

      <div className="lp-right-panel" style={{
        width: "42%",
        background: "linear-gradient(135deg, #0F3D3E 0%, #0a2c2d 40%, #061e1f 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden",
      }}>
        <style>{`
          @keyframes floatA { 0%,100%{transform:translateY(0) rotate(0deg);} 50%{transform:translateY(-18px) rotate(3deg);} }
          @keyframes floatB { 0%,100%{transform:translateY(0) rotate(0deg);} 50%{transform:translateY(-12px) rotate(-2deg);} }
          @keyframes floatC { 0%,100%{transform:translateY(0) rotate(0deg);} 50%{transform:translateY(-22px) rotate(4deg);} }
        `}</style>

        <svg viewBox="0 0 480 560" style={{ width: "88%", maxWidth: 420, position: "relative", zIndex: 1 }}
          fill="none" xmlns="http://www.w3.org/2000/svg">
          <Cube x={160} y={40} size={80} topColor="#2fb8a8" leftColor="#1a8a8a" rightColor="#116060" anim="floatC" delay="0s" />
          <Cube x={240} y={40} size={80} topColor="#25a89a" leftColor="#178080" rightColor="#0d5050" anim="floatB" delay="0.3s" />
          <Cube x={320} y={40} size={80} topColor="#1a8a8a" leftColor="#116060" rightColor="#094040" anim="floatA" delay="0.6s" />
          <Cube x={80} y={110} size={80} topColor="#25a89a" leftColor="#178080" rightColor="#0d5050" anim="floatB" delay="0.2s" />
          <Cube x={160} y={110} size={80} topColor="#2fb8a8" leftColor="#1a8a8a" rightColor="#116060" anim="floatA" delay="0.5s" />
          <Cube x={240} y={110} size={80} topColor="#35c8b8" leftColor="#22a090" rightColor="#147070" anim="floatC" delay="0.1s" />
          <Cube x={320} y={110} size={80} topColor="#25a89a" leftColor="#178080" rightColor="#0d5050" anim="floatB" delay="0.4s" />
          <Cube x={80} y={180} size={80} topColor="#1a8a8a" leftColor="#116060" rightColor="#094040" anim="floatA" delay="0.6s" />
          <Cube x={160} y={180} size={80} topColor="#35c8b8" leftColor="#22a090" rightColor="#147070" anim="floatC" delay="0.2s" />
          <Cube x={240} y={180} size={80} topColor="#2fb8a8" leftColor="#1a8a8a" rightColor="#116060" anim="floatB" delay="0.5s" />
          <Cube x={320} y={180} size={80} topColor="#1a8a8a" leftColor="#116060" rightColor="#094040" anim="floatA" delay="0.3s" />
          <Cube x={400} y={180} size={80} topColor="#25a89a" leftColor="#178080" rightColor="#0d5050" anim="floatC" delay="0s" />
          <Cube x={80} y={250} size={80} topColor="#25a89a" leftColor="#178080" rightColor="#0d5050" anim="floatB" delay="0.4s" />
          <Cube x={160} y={250} size={80} topColor="#1a8a8a" leftColor="#116060" rightColor="#094040" anim="floatA" delay="0.1s" />
          <Cube x={240} y={250} size={80} topColor="#35c8b8" leftColor="#22a090" rightColor="#147070" anim="floatC" delay="0.5s" />
          <Cube x={320} y={250} size={80} topColor="#2fb8a8" leftColor="#1a8a8a" rightColor="#116060" anim="floatB" delay="0.2s" />
          <Cube x={160} y={320} size={80} topColor="#1a8a8a" leftColor="#116060" rightColor="#094040" anim="floatA" delay="0.3s" opacity={0.6} />
          <Cube x={240} y={320} size={80} topColor="#25a89a" leftColor="#178080" rightColor="#0d5050" anim="floatB" delay="0.6s" opacity={0.6} />
          <Cube x={320} y={320} size={80} topColor="#1a8a8a" leftColor="#116060" rightColor="#094040" anim="floatC" delay="0s" opacity={0.6} />
          <Cube x={240} y={390} size={80} topColor="#1a8a8a" leftColor="#116060" rightColor="#094040" anim="floatA" delay="0.2s" opacity={0.3} />
        </svg>

        <div style={{
          position: "absolute", top: "30%", left: "50%",
          transform: "translate(-50%,-50%)", width: 300, height: 300,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(47,184,168,0.15) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
      </div>
    </div>
  );
}

function Cube({ x, y, size, topColor, leftColor, rightColor, anim, delay, opacity = 1 }) {
  const s = size, h = s * 0.5, q = s * 0.25;
  const top = `${x},${y + q} ${x + h},${y} ${x + s},${y + q} ${x + h},${y + h}`;
  const left = `${x},${y + q} ${x + h},${y + h} ${x + h},${y + h + q} ${x},${y + h}`;
  const right = `${x + h},${y + h} ${x + s},${y + q} ${x + s},${y + h} ${x + h},${y + h + q}`;
  return (
    <g style={{ opacity, animation: `${anim} ${3 + Math.random() * 1.5}s ease-in-out infinite`, animationDelay: delay }}>
      <polygon points={top} fill={topColor} />
      <polygon points={left} fill={leftColor} />
      <polygon points={right} fill={rightColor} />
    </g>
  );
}
